import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Flex,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  VStack,
  HStack,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Divider,
  IconButton,
  Tooltip,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ButtonGroup,
  Textarea,
  SimpleGrid,
  Image,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  InputGroup,
  InputLeftElement,
  TableContainer,
  Select,
  Stack,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiUpload, FiDownload, FiInfo, FiCopy, FiEdit, FiTrash2, FiCheck, FiX, FiSearch, FiFilter } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { secureProjectApi as projectApi } from '../services/csrfApi';
import FileUploader from '../components/FileUploader';
import { useThumbnailCache } from '../contexts/ThumbnailCacheContext';
import ReferenceDataManager from '../components/ReferenceDataManager';
import OriginalPromptManager from '../components/OriginalPromptManager';
import BlockConfigManager from '../components/BlockConfigManager';

// サムネイル画像コンポーネント - キャッシュを利用する堅牢な画像表示
const OcrThumbnail = ({ resultId, projectId, size = "sm" }) => {
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const urlsRef = useRef([]);
  
  // サムネイルキャッシュを利用
  const { addToCache, getFromCache, hasInCache } = useThumbnailCache();
  
  // キャッシュキーの作成
  const cacheKey = `thumbnail_${projectId}_${resultId}`;
  
  // サイズに基づく設定
  const dimensions = {
    sm: { width: "80px", height: "160px" },
    md: { width: "120px", height: "240px" },
    lg: { width: "160px", height: "320px" }
  };
  const { width, height } = dimensions[size] || dimensions.sm;
  
  // 初期化時にURLリストを構築
  useEffect(() => {
    // まずキャッシュを確認
    if (hasInCache(cacheKey)) {
      const cachedUrl = getFromCache(cacheKey);
      if (cachedUrl) {
        console.log(`サムネイル(${resultId}): キャッシュから読み込みました`);
        setImageUrl(cachedUrl);
        setIsLoaded(true);
        return;
      }
    }
    
    // キャッシュになければURLリストを構築
    // タイムスタンプ部分を抽出
    const timestamp = resultId.includes('_') 
      ? resultId.split('_')[0] 
      : resultId;
    
    const baseUrl = process.env.REACT_APP_API_URL || 'https://ocr-1.genxinc.ai';
    // /api/ が重複しないようにする
    const cleanBaseUrl = baseUrl.endsWith('/api') 
      ? baseUrl.substring(0, baseUrl.length - 4) 
      : baseUrl;
    
    // 複数の画像URLを生成（優先順位順）
    urlsRef.current = [
      // 1. PDF用サムネイル（最優先）
      `${cleanBaseUrl}/api/direct-image/${projectId}/${timestamp}.pdf?thumbnail=true`,

      // 2. direct-imageエンドポイント（サムネイル用）
      `${cleanBaseUrl}/api/direct-image/${projectId}/${timestamp}?thumbnail=true`,

      // 3. 拡張子付きdirect-image（サムネイル用）
      `${cleanBaseUrl}/api/direct-image/${projectId}/${timestamp}.jpg?thumbnail=true`,
      `${cleanBaseUrl}/api/direct-image/${projectId}/${timestamp}.png?thumbnail=true`,

      // 4. 静的ファイルアクセス
      `${cleanBaseUrl}/projects/${projectId}/images/${timestamp}.jpg`,
      `${cleanBaseUrl}/projects/${projectId}/images/${timestamp}.png`
    ];
    
    // 初期状態を設定
    setCurrentUrlIndex(0);
    setImageUrl(urlsRef.current[0]);
    setIsLoaded(false);
    setIsError(false);
  }, [resultId, projectId, cacheKey, hasInCache, getFromCache]);
  
  // 画像読み込み成功時の処理
  const handleImageLoad = () => {
    setIsLoaded(true);
    // キャッシュに成功したURLを保存
    addToCache(cacheKey, imageUrl);
    console.log(`サムネイル(${resultId}): 画像読み込み成功、キャッシュに保存: ${imageUrl}`);
  };
  
  // 画像読み込みエラー時の処理
  const handleImageError = async () => {
    // まだ試せるURLがあれば次を試す
    if (currentUrlIndex < urlsRef.current.length - 1) {
      console.log(`サムネイル(${resultId}): URL ${currentUrlIndex + 1}/${urlsRef.current.length} 失敗 - 次を試行`);
      const nextIndex = currentUrlIndex + 1;
      setCurrentUrlIndex(nextIndex);
      setImageUrl(urlsRef.current[nextIndex]);
    } else {
      // すべてのURLが失敗
      console.log(`サムネイル(${resultId}): すべてのURL失敗`);
      setIsError(true);
    }
  };
  
  return (
    <Box 
      w={width}
      h={height}
      borderRadius="md" 
      overflow="hidden"
      position="relative"
      bg="gray.100"
      flexShrink={0}
    >
      {!isError ? (
        <Image 
          src={imageUrl}
          alt="OCRイメージ"
          w="100%"
          h="100%"
          objectFit="cover"
          onLoad={handleImageLoad}
          onError={handleImageError}
          fallback={
            <Center h="100%">
              <Spinner size="sm" color="gray.400" />
            </Center>
          }
          crossOrigin="anonymous"
        />
      ) : (
        <Center h="100%" bg="gray.200">
          <Text fontSize="xs" color="gray.500" textAlign="center" px={1}>
            画像なし
          </Text>
        </Center>
      )}
    </Box>
  );
};

const Project = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  
  // 特権管理権限があるかを判定
  const hasAdminPrivilege = currentUser && (currentUser.role === 'admin' || currentUser.role === 'privileged');
  const toast = useToast();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  
  const [ocrResults, setOcrResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  
  // フィルタリング用の状態
  const [searchText, setSearchText] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // ページング用の状態
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  
  const { isOpen: isResultOpen, onOpen: onResultOpen, onClose: onResultClose } = useDisclosure();
  
  // ファイル形式を取得する関数
  const getFileExtension = (fileName) => {
    if (!fileName) return 'unknown';
    const extension = fileName.split('.').pop()?.toLowerCase();
    return extension || 'unknown';
  };
  
  // フィルタリングとソート機能
  const filteredAndSortedResults = React.useMemo(() => {
    let filtered = [...ocrResults];
    
    // テキスト検索フィルター
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(result => 
        result.originalFileName?.toLowerCase().includes(searchLower) ||
        result.raw_text?.toLowerCase().includes(searchLower) ||
        result.id.includes(searchText)
      );
    }
    
    // ファイル形式フィルター
    if (typeFilter !== 'all') {
      filtered = filtered.filter(result => {
        const fileExt = getFileExtension(result.originalFileName);
        if (typeFilter === 'pdf') return fileExt === 'pdf';
        if (typeFilter === 'image') return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExt);
        return true;
      });
    }
    
    // 日付フィルター
    if (dateFilter && dateFilter !== 'custom') {
      const now = new Date();
      const filterDate = new Date();
      
      if (dateFilter === 'today') {
        filterDate.setHours(0, 0, 0, 0);
      } else if (dateFilter === 'week') {
        filterDate.setDate(now.getDate() - 7);
      } else if (dateFilter === 'month') {
        filterDate.setMonth(now.getMonth() - 1);
      }
      
      filtered = filtered.filter(result => 
        result.processedDate >= filterDate
      );
    }
    
    // カスタム日付範囲フィルター
    if (dateFilter === 'custom' && (customDateFrom || customDateTo)) {
      filtered = filtered.filter(result => {
        const resultDate = result.processedDate;
        let isInRange = true;
        
        if (customDateFrom) {
          const fromDate = new Date(customDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          isInRange = isInRange && resultDate >= fromDate;
        }
        
        if (customDateTo) {
          const toDate = new Date(customDateTo);
          toDate.setHours(23, 59, 59, 999);
          isInRange = isInRange && resultDate <= toDate;
        }
        
        return isInRange;
      });
    }
    
    // ソート
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = a.processedDate;
          bValue = b.processedDate;
          break;
        case 'filename':
          aValue = a.originalFileName || '';
          bValue = b.originalFileName || '';
          break;
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [ocrResults, searchText, typeFilter, dateFilter, customDateFrom, customDateTo, sortBy, sortOrder]);
  
  // ページングの計算
  const totalPages = Math.ceil(filteredAndSortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = filteredAndSortedResults.slice(startIndex, startIndex + itemsPerPage);
  
  // フィルタやソートが変更された時は1ページ目に戻る
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, typeFilter, dateFilter, customDateFrom, customDateTo, sortBy, sortOrder]);
  
  // 画像URLの基本部分を適切に生成する関数
  const getBaseImageUrl = () => {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://ocr-1.genxinc.ai';
    // /api/ が重複しないようにする
    return baseUrl.endsWith('/api') 
      ? baseUrl.substring(0, baseUrl.length - 4) 
      : baseUrl;
  };
  
  // OCR結果IDから最適な画像URLを生成する関数
  const getImageUrlForResult = (resultId) => {
    // OCR結果の状態によって最適なURL構築方法を決定
    // 1. まず、タイムスタンプ部分を抽出
    const timestamp = resultId.includes('_') 
      ? resultId.split('_')[0] 
      : resultId;
    
    // 2. 複数の候補を用意し、配列として返す
    return [
      // 優先度1: 完全なOCR結果IDをそのまま使用
      `${getBaseImageUrl()}/api/direct-image/${projectId}/${timestamp}`,
      
      // 優先度2: 日付部分(YYYYMMDD)のみを使用
      `${getBaseImageUrl()}/api/direct-image/${projectId}/${timestamp.substring(0, 8)}`,
      
      // 優先度3: タイムスタンプの前にある日付部分を先頭から探す試み
      ...(timestamp.match(/\d{8}/) 
        ? [`${getBaseImageUrl()}/api/direct-image/${projectId}/${timestamp.match(/\d{8}/)[0]}`] 
        : []),
        
      // 優先度4: 拡張子付きバージョン
      `${getBaseImageUrl()}/api/direct-image/${projectId}/${timestamp}.jpg`,
      `${getBaseImageUrl()}/api/direct-image/${projectId}/${timestamp}.png`,
      
      // 優先度5: 代替エンドポイント
      `${getBaseImageUrl()}/api/projects/${projectId}/public-image/${timestamp}`
    ];
  };
  
  // プロジェクト情報の取得
  const fetchProject = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectApi.getProject(projectId);
      setProject(response.data);
      setEditedName(response.data.name);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      setError('プロジェクト情報の取得に失敗しました。');
      
      // 404エラーの場合はダッシュボードにリダイレクト
      if (error.response?.status === 404) {
        toast({
          title: 'プロジェクトが見つかりません',
          description: '指定されたプロジェクトが存在しないか、アクセス権限がありません。',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // OCR結果一覧の取得
  const fetchOcrResults = async () => {
    setResultLoading(true);
    
    try {
      const response = await projectApi.getOcrResults(projectId);
      console.log('OCR結果一覧:', response.data);
      
      // 各OCR結果のデータが必要な情報を持っているか確認
      const enhancedResults = response.data.map(result => {
        // 結果IDからタイムスタンプを取得
        const timestamp = result.id;
        // 日時をフォーマット
        const dateTime = new Date(
          timestamp.replace(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1-$2-$3T$4:$5:$6')
        );
        
        return {
          ...result,
          // 元のファイル名 (APIから取得、ない場合はタイムスタンプベース)
          originalFileName: result.original_file_name || result.file_name || `${timestamp}${result.page_count > 1 ? '.pdf' : '.jpg'}`,
          // 処理日時をDate型で提供
          processedDate: dateTime,
          // 処理日時の表示用文字列
          processedDateFormatted: dateTime.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }),
        };
      });
      
      setOcrResults(enhancedResults);
    } catch (error) {
      console.error('Failed to fetch OCR results:', error);
      toast({
        title: 'OCR結果の取得に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setResultLoading(false);
    }
  };
  
  // プロジェクト名の更新
  const handleUpdateName = async () => {
    if (!editedName.trim() || editedName === project.name) {
      setIsEditing(false);
      return;
    }
    
    try {
      const response = await projectApi.updateProject(projectId, { name: editedName });
      setProject(response.data);
      toast({
        title: 'プロジェクト名を更新しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to update project name:', error);
      toast({
        title: 'プロジェクト名の更新に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      // 元の名前に戻す
      setEditedName(project.name);
    } finally {
      setIsEditing(false);
    }
  };
  
  // ファイルアップロード処理
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    // AIプロバイダーはOCR処理画面で選択するため、デフォルト値を使用
    formData.append('ai_provider', 'openai');
    
    try {
      const response = await projectApi.processFile(projectId, formData);
      
      // アップロード成功
      toast({
        title: 'ファイルのアップロードに成功しました',
        description: 'OCR処理が完了しました。',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // OCR結果一覧を再取得
      await fetchOcrResults();
      
      // プロジェクト情報も更新（履歴カウント更新のため）
      await fetchProject();
      
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast({
        title: 'ファイルのアップロードに失敗しました',
        description: error.response?.data?.detail || 'エラーが発生しました。',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  // OCR結果の詳細ページに遷移
  const handleViewResult = (resultId) => {
    navigate(`/project/${projectId}/result/${resultId}`);
  };
  
  // クリップボードにコピー
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'クリップボードにコピーしました',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };
  
  // マウント時にプロジェクト情報とOCR結果一覧を取得
  useEffect(() => {
    fetchProject();
    fetchOcrResults();
  }, [projectId]);
  
  // ローディング中
  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="50vh">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
      </Flex>
    );
  }
  
  // エラー時
  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }
  
  return (
    <Box>
      {/* プロジェクトヘッダー */}
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        bg={useColorModeValue('white', 'gray.800')}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
      >
        {isEditing ? (
          <Flex align="center">
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              mr={2}
              w="auto"
            />
            <ButtonGroup size="sm">
              <IconButton
                icon={<FiCheck />}
                colorScheme="green"
                onClick={handleUpdateName}
                aria-label="保存"
              />
              <IconButton
                icon={<FiX />}
                colorScheme="red"
                    onClick={async () => {
                      if (window.confirm("本当にこのプロジェクトを削除しますか？この操作は元に戻すことができません。")) {
                        try {
                          await projectApi.deleteProject(projectId);
                          toast({
                            title: "プロジェクトを削除しました",
                            status: "success",
                            duration: 3000,
                            isClosable: true,
                          });
                          navigate("/");
                        } catch (error) {
                          toast({
                            title: "プロジェクトの削除に失敗しました",
                            description: error.response?.data?.detail || "エラーが発生しました",
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      }
                    }}
                aria-label="キャンセル"
              />
            </ButtonGroup>
          </Flex>
        ) : (
          <HStack>
            <Heading size="lg">{project.name}</Heading>
            <Tooltip label="プロジェクト名を編集">
              <IconButton
                icon={<FiEdit />}
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                aria-label="編集"
              />
            </Tooltip>
          </HStack>
        )}
        
        <HStack spacing={3}>
          <Badge colorScheme="blue">{project.history_count} 件の履歴</Badge>
          <Box fontSize="sm" color="gray.500">
            作成日: {new Date(project.created_at).toLocaleDateString()}
          </Box>
          <Box fontSize="sm" color="gray.500">
            更新日: {new Date(project.updated_at).toLocaleDateString()}
          </Box>
        </HStack>
      </Flex>
      
      {/* メインコンテンツ */}
      <Tabs variant="enclosed" borderColor="gray.200">
        <TabList>
          <Tab>OCR処理</Tab>
          <Tab>履歴</Tab>
          <Tab>参照データ</Tab>
          <Tab>オリジナルプロンプト</Tab>
          <Tab>ブロック設定</Tab>
          {hasAdminPrivilege && <Tab>設定</Tab>}
        </TabList>
        
        <TabPanels>
          {/* OCR処理タブ */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                p={6}
                borderRadius="lg"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <Heading size="md">OCR処理</Heading>
                  <Text>PDFまたは画像ファイルをアップロードして、テキストを抽出します。</Text>
                  
                  
                  <Button
                    colorScheme="brand"
                    size="lg"
                    leftIcon={<FiUpload />}
                    onClick={() => navigate(`/project/${projectId}/ocr-processing`)}
                    w="100%"
                    py={8}
                  >
                    新規OCR処理を開始
                  </Button>
                </VStack>
              </Box>
              
              {/* OCR処理結果 */}
              <Box>
                <Heading size="md" mb={4}>OCR処理結果</Heading>
                
                {/* フィルター・検索コントロール */}
                <Box mb={4} p={4} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md">
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                      {/* テキスト検索 */}
                      <FormControl>
                        <FormLabel fontSize="sm">検索</FormLabel>
                        <InputGroup>
                          <InputLeftElement>
                            <FiSearch />
                          </InputLeftElement>
                          <Input
                            placeholder="ファイル名、結果ID、内容で検索"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="sm"
                          />
                        </InputGroup>
                      </FormControl>
                      
                      {/* ファイル形式フィルター */}
                      <FormControl>
                        <FormLabel fontSize="sm">ファイル形式</FormLabel>
                        <Select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          size="sm"
                        >
                          <option value="all">すべて</option>
                          <option value="pdf">PDF</option>
                          <option value="image">画像 (JPG, PNG等)</option>
                        </Select>
                      </FormControl>
                      
                      {/* 日付フィルター */}
                      <FormControl>
                        <FormLabel fontSize="sm">期間</FormLabel>
                        <Select
                          value={dateFilter}
                          onChange={(e) => {
                            setDateFilter(e.target.value);
                            // 日付範囲指定以外が選択された場合、カスタム日付をクリア
                            if (e.target.value !== 'custom') {
                              setCustomDateFrom('');
                              setCustomDateTo('');
                            }
                          }}
                          size="sm"
                        >
                          <option value="">すべて</option>
                          <option value="today">今日</option>
                          <option value="week">過去1週間</option>
                          <option value="month">過去1ヶ月</option>
                          <option value="custom">日付範囲指定</option>
                        </Select>
                      </FormControl>
                      
                      {/* ソート */}
                      <FormControl>
                        <FormLabel fontSize="sm">並び順</FormLabel>
                        <HStack>
                          <Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            size="sm"
                          >
                            <option value="date">処理日時</option>
                            <option value="filename">ファイル名</option>
                            <option value="id">結果ID</option>
                          </Select>
                          <IconButton
                            size="sm"
                            aria-label="ソート順序"
                            icon={sortOrder === 'asc' ? <FiUpload /> : <FiDownload />}
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                          />
                        </HStack>
                      </FormControl>
                    </SimpleGrid>
                    
                    {/* 日付範囲指定（カスタム選択時のみ表示） */}
                    {dateFilter === 'custom' && (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel fontSize="sm">開始日</FormLabel>
                          <Input
                            type="date"
                            value={customDateFrom}
                            onChange={(e) => setCustomDateFrom(e.target.value)}
                            size="sm"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">終了日</FormLabel>
                          <Input
                            type="date"
                            value={customDateTo}
                            onChange={(e) => setCustomDateTo(e.target.value)}
                            size="sm"
                          />
                        </FormControl>
                      </SimpleGrid>
                    )}
                  </VStack>
                  
                  {/* 結果件数表示 */}
                  <Flex justify="space-between" align="center" mt={2}>
                    <Text fontSize="sm" color="gray.600">
                      {filteredAndSortedResults.length} 件中 {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredAndSortedResults.length)} 件を表示
                    </Text>
                    {totalPages > 1 && (
                      <Text fontSize="sm" color="gray.600">
                        ページ {currentPage} / {totalPages}
                      </Text>
                    )}
                  </Flex>
                </Box>
                
                {resultLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                ) : filteredAndSortedResults.length === 0 ? (
                  <Box
                    p={8}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    borderRadius="md"
                    textAlign="center"
                  >
                    <Text>
                      {ocrResults.length === 0 ? 'OCR処理結果がありません' : '検索条件に一致する結果がありません'}
                    </Text>
                  </Box>
                ) : (
                  <Box
                    bg={useColorModeValue('white', 'gray.800')}
                    borderRadius="md"
                    boxShadow="sm"
                    overflow="hidden"
                  >
                    <TableContainer>
                      <Table variant="simple" size="md">
                        <Thead bg={useColorModeValue('gray.50', 'gray.700')}>
                          <Tr>
                            <Th width="80px">画像</Th>
                            <Th width="100px">ファイル形式</Th>
                            <Th width="120px">結果ID</Th>
                            <Th width="160px">処理日時</Th>
                            <Th width="200px">元ファイル名</Th>
                            {hasAdminPrivilege && <Th width="120px">AIプロバイダー</Th>}
                            <Th width="100px">アクション</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {paginatedResults.map((result) => (
                            <Tr key={result.id} _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}>
                              <Td>
                                <OcrThumbnail 
                                  resultId={result.id} 
                                  projectId={projectId}
                                  size="sm" 
                                />
                              </Td>
                              <Td>
                                <Badge 
                                  colorScheme={
                                    getFileExtension(result.originalFileName) === 'pdf' ? 'red' :
                                    ['jpg', 'jpeg', 'png', 'gif'].includes(getFileExtension(result.originalFileName)) ? 'green' :
                                    'gray'
                                  }
                                  size="md"
                                >
                                  {getFileExtension(result.originalFileName).toUpperCase()}
                                </Badge>
                              </Td>
                              <Td>
                                <Text 
                                  fontSize="md" 
                                  fontWeight="bold"
                                  color="brand.500"
                                  cursor="pointer"
                                  _hover={{ textDecoration: "underline", color: "brand.600" }}
                                  onClick={() => handleViewResult(result.id)}
                                >
                                  #{result.id.substring(8)}
                                </Text>
                              </Td>
                              <Td>
                                <Text 
                                  fontSize="md"
                                  color="brand.500"
                                  cursor="pointer"
                                  _hover={{ textDecoration: "underline", color: "brand.600" }}
                                  onClick={() => handleViewResult(result.id)}
                                >
                                  {result.processedDateFormatted}
                                </Text>
                              </Td>
                              <Td>
                                <Text 
                                  fontSize="md" 
                                  noOfLines={2} 
                                  maxW="180px"
                                  color="brand.500"
                                  cursor="pointer"
                                  _hover={{ textDecoration: "underline", color: "brand.600" }}
                                  onClick={() => handleViewResult(result.id)}
                                >
                                  {result.originalFileName || "不明"}
                                </Text>
                              </Td>
                              {hasAdminPrivilege && (
                                <Td>
                                  <Badge 
                                    colorScheme={
                                      result.ai_provider === 'openai' ? 'green' :
                                      result.ai_provider === 'claude' ? 'purple' :
                                      result.ai_provider === 'mistral' ? 'orange' :
                                      result.ai_provider === 'gemini' ? 'blue' :
                                      'gray'
                                    }
                                    size="md"
                                  >
                                    {result.ai_provider ? 
                                      (result.ai_provider === 'openai' ? 'OpenAI' :
                                       result.ai_provider === 'claude' ? 'Claude' :
                                       result.ai_provider === 'mistral' ? 'Mistral' :
                                       result.ai_provider === 'gemini' ? 'Gemini' :
                                       result.ai_provider.toUpperCase()) 
                                      : '不明'
                                    }
                                  </Badge>
                                </Td>
                              )}
                              <Td>
                                <Button
                                  size="md"
                                  leftIcon={<FiInfo />}
                                  onClick={() => handleViewResult(result.id)}
                                  colorScheme="brand"
                                  variant="outline"
                                >
                                  詳細
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                    
                    {/* ページングナビゲーション */}
                    {totalPages > 1 && (
                      <Box mt={4} px={4} pb={4}>
                        <Flex justify="center" align="center">
                          <Stack direction="row" spacing={2}>
                            <Button
                              size="sm"
                              onClick={() => setCurrentPage(1)}
                              isDisabled={currentPage === 1}
                              variant="outline"
                            >
                              最初
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              isDisabled={currentPage === 1}
                              variant="outline"
                            >
                              前
                            </Button>
                            
                            {/* ページ番号ボタン */}
                            {[...Array(totalPages)].map((_, index) => {
                              const pageNum = index + 1;
                              const isCurrentPage = pageNum === currentPage;
                              
                              // 現在のページから前後2ページを表示
                              if (
                                pageNum === 1 ||
                                pageNum === totalPages ||
                                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                              ) {
                                return (
                                  <Button
                                    key={pageNum}
                                    size="sm"
                                    onClick={() => setCurrentPage(pageNum)}
                                    colorScheme={isCurrentPage ? "brand" : "gray"}
                                    variant={isCurrentPage ? "solid" : "outline"}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              } else if (
                                (pageNum === currentPage - 3 && currentPage > 4) ||
                                (pageNum === currentPage + 3 && currentPage < totalPages - 3)
                              ) {
                                return (
                                  <Text key={pageNum} size="sm" color="gray.500" mx={1}>
                                    ...
                                  </Text>
                                );
                              }
                              return null;
                            })}
                            
                            <Button
                              size="sm"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              isDisabled={currentPage === totalPages}
                              variant="outline"
                            >
                              次
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setCurrentPage(totalPages)}
                              isDisabled={currentPage === totalPages}
                              variant="outline"
                            >
                              最後
                            </Button>
                          </Stack>
                        </Flex>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </VStack>
          </TabPanel>
          
          {/* 履歴タブ */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                p={6}
                borderRadius="lg"
                boxShadow="sm"
              >
                <Heading size="md" mb={4}>OCR処理履歴</Heading>
                
                {resultLoading ? (
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                ) : ocrResults.length === 0 ? (
                  <Box
                    p={8}
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    borderRadius="md"
                    textAlign="center"
                  >
                    <Text>OCR処理履歴がありません</Text>
                  </Box>
                ) : (
                  <VStack spacing={4} align="stretch">
                    {ocrResults.map((result) => (
                      <Card key={result.id} variant="outline">
                        <CardHeader>
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="bold">
                              結果 #{result.id.substring(8)}
                            </Text>
                            <IconButton
                              icon={<FiInfo />}
                              size="sm"
                              aria-label="詳細を表示"
                              onClick={() => handleViewResult(result.id)}
                            />
                          </Flex>
                        </CardHeader>
                        <CardBody>
                          <Flex>
                            {/* サムネイル画像 */}
                            <Box mr={4}>
                              <OcrThumbnail 
                                resultId={result.id} 
                                projectId={projectId} 
                                size="sm" 
                              />
                            </Box>
                            
                            {/* 詳細情報部分 */}
                            <VStack align="start" flex="1" spacing={1}>
                              {/* 処理時間 */}
                              <HStack>
                                <Text fontSize="sm" fontWeight="bold" color="gray.600" width="80px">
                                  処理時間:
                                </Text>
                                <Text fontSize="sm" color="gray.700">
                                  {result.processedDateFormatted}
                                </Text>
                              </HStack>
                              
                              {/* 元ファイル名 */}
                              <HStack>
                                <Text fontSize="sm" fontWeight="bold" color="gray.600" width="80px">
                                  元ファイル:
                                </Text>
                                <Text fontSize="sm" color="gray.700" noOfLines={1}>
                                  {result.originalFileName || "不明"}
                                </Text>
                              </HStack>
                              
                              {/* AIプロバイダー（特権管理権限ユーザーのみ） */}
                              {hasAdminPrivilege && (
                                <HStack>
                                  <Text fontSize="sm" fontWeight="bold" color="gray.600" width="80px">
                                    AI:
                                  </Text>
                                  <Badge 
                                    colorScheme={
                                      result.ai_provider === 'openai' ? 'green' :
                                      result.ai_provider === 'claude' ? 'purple' :
                                      result.ai_provider === 'mistral' ? 'orange' :
                                      result.ai_provider === 'gemini' ? 'blue' :
                                      'gray'
                                    }
                                    size="sm"
                                  >
                                    {result.ai_provider ? 
                                      (result.ai_provider === 'openai' ? 'OpenAI' :
                                       result.ai_provider === 'claude' ? 'Claude' :
                                       result.ai_provider === 'mistral' ? 'Mistral' :
                                       result.ai_provider === 'gemini' ? 'Gemini' :
                                       result.ai_provider.toUpperCase()) 
                                      : '不明'
                                    }
                                  </Badge>
                                </HStack>
                              )}

                              {/* テキスト内容（一部） */}
                              <HStack alignItems="flex-start" mt={2}>
                                <Text fontSize="sm" fontWeight="bold" color="gray.600" width="80px">
                                  抽出結果:
                                </Text>
                                <Text fontSize="sm" noOfLines={2} flex="1">
                                  {result.raw_text.substring(0, 200)}...
                                </Text>
                              </HStack>
                            </VStack>
                          </Flex>
                        </CardBody>
                      </Card>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </TabPanel>
          
          {/* 参照データタブ */}
          <TabPanel>
            <ReferenceDataManager projectId={projectId} />
          </TabPanel>
          
          {/* オリジナルプロンプトタブ */}
          <TabPanel>
            <OriginalPromptManager projectId={projectId} />
          </TabPanel>
          
          {/* ブロック設定タブ */}
          <TabPanel>
            <BlockConfigManager projectId={projectId} />
          </TabPanel>
          
          {/* 設定タブ */}
          {hasAdminPrivilege && (
            <TabPanel>
            <VStack spacing={6} align="stretch">
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                p={6}
                borderRadius="lg"
                boxShadow="sm"
              >
                <Heading size="md" mb={4}>プロジェクト設定</Heading>
                
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>プロジェクト名</FormLabel>
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                    />
                  </FormControl>
                  
                  <Button
                    colorScheme="brand"
                    onClick={handleUpdateName}
                    isDisabled={!editedName.trim() || editedName === project.name}
                  >
                    更新
                  </Button>
                </VStack>
              </Box>
              
              {/* 危険な操作 */}
              <Box
                bg={useColorModeValue('red.50', 'red.900')}
                p={6}
                borderRadius="lg"
                boxShadow="sm"
              >
                <Heading size="md" color="red.500" mb={4}>危険な操作</Heading>
                
                <VStack spacing={4} align="stretch">
                  <Text>
                    以下の操作は元に戻すことができません。十分に注意して実行してください。
                  </Text>
                  
                  <Button
                    colorScheme="red"
                    leftIcon={<FiTrash2 />}
                    onClick={async () => {
                      if (window.confirm("本当にこのプロジェクトを削除しますか？この操作は元に戻すことができません。")) {
                        try {
                          await projectApi.deleteProject(projectId);
                          toast({
                            title: "プロジェクトを削除しました",
                            status: "success",
                            duration: 3000,
                            isClosable: true,
                          });
                          navigate("/");
                        } catch (error) {
                          toast({
                            title: "プロジェクトの削除に失敗しました",
                            description: error.response?.data?.detail || "エラーが発生しました",
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      }
                    }}
                  >
                    プロジェクトを削除
                  </Button>
                </VStack>
              </Box>
            </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
      
{/* モーダルは不要になりました。OCR結果は専用ページで表示します */}
    </Box>
  );
};

export default Project;